import React, { useState, useEffect } from 'react';
import { flexRender, type Table, type ColumnDef } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronRight, ChevronDown } from 'lucide-react';
import CellWithTooltip from './CellWithTooltip';
import type { DataTableColumn } from '../types';
import { getCommonPinningStyles } from "./commonStyles";

interface DataTableBodyProps<TData> {
  table: Table<TData>;
  columns: ColumnDef<TData>[];
  emptyMessage?: string;
  enableVirtualization?: boolean;
  enableColumnResizing?: boolean;
  rowHeight?: number;
  overscan?: number;
  parentRef?: React.RefObject<HTMLDivElement>;
  rowClickable?: boolean;
  onRowClick?: (row: TData, columnId: string) => void;
  hideLeafRows?: boolean;
}

export default function DataTableBody<TData>({
  table,
  columns,
  emptyMessage = 'No data available',
  enableVirtualization = false,
  enableColumnResizing = false,
  rowHeight = 52,
  overscan = 200,
  parentRef,
  rowClickable = false,
  onRowClick,
  hideLeafRows = false,
}: DataTableBodyProps<TData>) {
  const allRows = table.getRowModel().rows;
  const rows = hideLeafRows ? allRows.filter(row => row.getIsGrouped()) : allRows;
  const grouping = table.getState().grouping;
  const [isRefReady, setIsRefReady] = useState(false);  

   useEffect(() => {
     if (!!parentRef?.current && !isRefReady) {
       setIsRefReady(true);
     }
   }, [parentRef, isRefReady]);

  const isWaitingForVirtual = enableVirtualization && !isRefReady;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef?.current || null,
    estimateSize: () => rowHeight,
    overscan,
    enabled: enableVirtualization && isRefReady,
    // Performance optimizations
    measureElement: typeof window !== 'undefined' && window.ResizeObserver
      ? (element) => element?.getBoundingClientRect().height ?? rowHeight
      : undefined,
    scrollMargin: 0,
    getItemKey: (index) => rows[index]?.id ?? index,
  });

  if (rows.length === 0 || isWaitingForVirtual) {
    return (
      <tbody>
        <tr>
          <td
            colSpan={columns.length}
            className="px-6 py-4 text-center text-gray-500"
          >
            {isWaitingForVirtual ? "Loading" : emptyMessage}
          </td>
        </tr>
      </tbody>
    );
  }

  const getRowClassName = (row: any) => {
    const depth = row.depth;
    if (depth === 0 && row.getIsGrouped()) {
      return 'bg-gray-50 font-medium border-t-2 border-gray-300';
    } else if (depth === 1 && row.getIsGrouped()) {
      return 'bg-gray-100/50 font-medium';
    } else if (row.getIsGrouped()) {
      return 'bg-gray-100/30';
    }
    return 'hover:bg-gray-50';
  };

  const getCellPadding = (depth: number, isFirstCell: boolean) => {
    if (!isFirstCell) return '';
    const basePadding = 24; // 6 * 4 (px-6)
    const indentPerLevel = 20;
    const totalPadding = basePadding + (depth * indentPerLevel);
    return `${totalPadding}px`;
  };

  const handleCellClick = (row: any, column: DataTableColumn<TData>, columnId: string) => {
    // Don't handle clicks on grouped or aggregated cells
    if (row.getIsGrouped()) return;

    // Check if this column has clicks disabled
    if (column.disableRowClick) return;

    // Use column-specific handler if available
    if (column.onCellClick) {
      column.onCellClick(row.original, column);
      return;
    }

    // Fall back to global row click handler
    if (rowClickable && onRowClick) {
      onRowClick(row.original, columnId);
    }
  };

  const getCellClickableClass = (column: DataTableColumn<TData>, row: any) => {
    if (row.getIsGrouped()) return '';
    if (column.disableRowClick) return '';
    if (column.onCellClick || (rowClickable && onRowClick)) {
      return 'cursor-pointer hover:bg-gray-100';
    }
    return '';
  };

  const renderRow = (row: any, index?: number, measureRef?: (el: HTMLTableRowElement | null) => void) => {
    return (
      <tr
        ref={measureRef}
        key={row.id}
        className={getRowClassName(row)}
        data-testid={row.getIsGrouped() ? `group-row-${row.id}` : `data-row-${row.id}`}
        data-index={index}
      >
        {row.getVisibleCells().map((cell: any, cellIndex: number) => {
          const isFirstCell = cellIndex === 0;
          const paddingLeft = grouping.length > 0 ? getCellPadding(row.depth, isFirstCell) : undefined;

          const columnDef = cell.column.columnDef as DataTableColumn<TData>;
          const isClickable = !row.getIsGrouped() && !columnDef.disableRowClick &&
                            (columnDef.onCellClick || (rowClickable && onRowClick));

          // Apply column width when resizing is enabled
          const cellStyle: React.CSSProperties = {
            paddingLeft: paddingLeft || '24px',
            paddingRight: '24px',
          };

          // Only apply width when column resizing is enabled to match header behavior
          if (enableColumnResizing) {
            cellStyle.width = cell.column.getSize();
          } else if (columnDef.size) {
            cellStyle.width = columnDef.size;
          }

          // Always apply min/max constraints
          if (columnDef.minSize) cellStyle.minWidth = columnDef.minSize;
          if (columnDef.maxSize) cellStyle.maxWidth = columnDef.maxSize;

          return (
            <td
              key={cell.id}
              className={`py-3 text-sm text-gray-900 ${getCellClickableClass(columnDef, row)}`}
              style={{
                ...cellStyle,
                ...getCommonPinningStyles(cell.column)
              }}
              onClick={isClickable ? () => handleCellClick(row, columnDef, cell.column.id) : undefined}
            >
              {cell.getIsGrouped() ? (
                <div className="flex items-center gap-2">
                  {!(hideLeafRows && row.depth === grouping.length - 1) && (
                    <button
                      onClick={row.getToggleExpandedHandler()}
                      className="hover:bg-gray-300 p-0.5 rounded"
                    >
                      {row.getIsExpanded() ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  <span className="font-semibold">
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </span>
                  <span className="text-gray-500 text-xs">
                    ({row.subRows.length})
                  </span>
                </div>
              ) : cell.getIsAggregated() ? (
                <span
                  className="font-medium text-gray-700"
                  data-testid={`aggregated-cell-${cell.column.id}`}
                >
                  {flexRender(
                    cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                    cell.getContext()
                  )}
                </span>
              ) : cell.getIsPlaceholder() ? (
                <span className="text-gray-400">—</span>
              ) : (
                  <CellWithTooltip
                    content={flexRender(cell.column.columnDef.cell, cell.getContext())}
                    minSize={(cell.column.columnDef as DataTableColumn<TData>).minSize}
                    maxSize={(cell.column.columnDef as DataTableColumn<TData>).maxSize}
                    enableTooltip={(cell.column.columnDef as DataTableColumn<TData>).enableTooltip}
                  />
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  if (enableVirtualization && parentRef) {
    const virtualItems = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();

    return (
      <tbody className="bg-white divide-y divide-gray-200">
        {virtualItems.length > 0 && (
          <>
            {/* Spacer for rows before the visible window */}
            {virtualItems[0].index > 0 && (
              <tr style={{ height: virtualItems[0].start }} aria-hidden="true">
                <td colSpan={columns.length} />
              </tr>
            )}

            {/* Render visible rows */}
            {virtualItems.map(virtualItem => {
              const row = rows[virtualItem.index];
              return renderRow(row, virtualItem.index, virtualizer.measureElement);
            })}

            {/* Spacer for rows after the visible window */}
            {virtualItems[virtualItems.length - 1].index < rows.length - 1 && (
              <tr
                style={{
                  height: totalSize - virtualItems[virtualItems.length - 1].end
                }}
                aria-hidden="true"
              >
                <td colSpan={columns.length} />
              </tr>
            )}
          </>
        )}
      </tbody>
    );
  }

  return (
    <tbody className="bg-white divide-y divide-gray-200">
      {rows.map((row, index) => renderRow(row, index))}
    </tbody>
  );
}
